import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, User, Tag, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEO/SEOHead';
import axios from 'axios';

const WORDPRESS_API = 'https://www.storespergolas.com/blog/wp-json/wp/v2';
const POSTS_PER_PAGE = 9;

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Charger les catégories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${WORDPRESS_API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Erreur chargement catégories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Charger les articles
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = {
          per_page: POSTS_PER_PAGE,
          page: currentPage,
          _embed: true
        };
        
        if (searchQuery) {
          params.search = searchQuery;
        }
        
        if (selectedCategory) {
          params.categories = selectedCategory;
        }

        const response = await axios.get(`${WORDPRESS_API}/posts`, { params });
        setPosts(response.data);
        
        // Récupérer le nombre total de pages depuis les headers
        const total = parseInt(response.headers['x-wp-totalpages'] || 1);
        setTotalPages(total);
      } catch (error) {
        console.error('Erreur chargement articles:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [currentPage, searchQuery, selectedCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Réinitialiser à la page 1 lors d'une recherche
  };

  const extractImageUrl = (post) => {
    if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }
    return '/images/blog-placeholder.jpg';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || '';
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Blog - Conseils et Actualités Stores & Pergolas"
        description="Découvrez nos articles sur les stores bannes, pergolas et voiles d'ombrage. Guides pratiques, conseils d'experts et actualités du secteur."
        url="/blog"
        breadcrumbs={[
          { name: 'Accueil', url: '/' },
          { name: 'Blog', url: '/blog' }
        ]}
      />

      <Header />

      {/* Hero Section */}
      <div className="bg-celadon text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Blog</h1>
          <p className="text-lg text-gray-200">
            Conseils d'experts, guides pratiques et actualités sur les stores et pergolas
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Barre de recherche et filtres */}
        <div className="mb-8 space-y-4">
          {/* Recherche */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
              />
            </div>
            <button
              type="submit"
              className="bg-celadon text-white px-6 py-3 rounded-lg hover:bg-celadon-light transition-colors"
            >
              Rechercher
            </button>
          </form>

          {/* Filtres par catégorie */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setSelectedCategory(null); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                !selectedCategory
                  ? 'bg-celadon text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Tous les articles
            </button>
            {categories.slice(0, 6).map((category) => (
              <button
                key={category.id}
                onClick={() => { setSelectedCategory(category.id); setCurrentPage(1); }}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-celadon text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Grille d'articles */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-celadon"></div>
            <p className="mt-4 text-gray-600">Chargement des articles...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucun article trouvé.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  {/* Image */}
                  <div className="relative h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={extractImageUrl(post)}
                      alt={stripHtml(post.title.rendered)}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  {/* Contenu */}
                  <div className="p-6">
                    {/* Catégorie */}
                    {post.categories && post.categories[0] && (
                      <div className="flex items-center gap-2 text-sm text-celadon mb-2">
                        <Tag size={16} />
                        <span>{getCategoryName(post.categories[0])}</span>
                      </div>
                    )}

                    {/* Titre */}
                    <h2
                      className="text-xl font-bold text-gray-900 mb-2 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                    />

                    {/* Extrait */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {stripHtml(post.excerpt.rendered)}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{new Date(post.date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>

                    {/* Bouton */}
                    <button className="text-celadon font-medium text-sm flex items-center gap-2 hover:gap-3 transition-all">
                      Lire la suite
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg ${
                        currentPage === i + 1
                          ? 'bg-celadon text-white'
                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
